<template>
  <v-card>
    <v-card-title>
      <span class="headline-2">{{ addOffersToDealMode ? $i18n.useDealPicker.ADD_OFFERS_TO_DEAL : $i18n.useDealPicker.LOAD_DEAL }}</span>
    </v-card-title>
    <v-divider/>
    <v-card-text class="ph-no-capture">
      <v-container fluid grid-list-lg>
        <div class="body-1 opacity-90 mb-6 mt-4 tracking-normal " v-if="modalData.additionalInfo">
          {{ modalData.additionalInfo }}
        </div>
        <raiffeisen-access-info
          v-if="isRaiffeisenOfferSelected && !hasAccessToRaiffeisen"
          :selected-offers-count="selectedOffersCount"/>
        <client-autocomplete
            :singular="true"
            :create="true"
            :is-company="false"
            :hide-client-create-type-tabs="true"
            v-model="selectedClient"
            :disabled="isRaiffeisenOfferSelected && selectedOffersCount === 1 && !hasAccessToRaiffeisen"
            @input="onClientPicked($event)"/>
        <template v-if="selectedClient">
          <fp-radio-group
              v-if="clientHasDeals"
              prepend-icon="none"
              class="mb-4 mt-3"
              row
              v-model="isExistingDeal"
              :items="dealRadioItems"/>
          <v-window v-model="isExistingDeal" class="mb-4">
            <v-window-item :value="true">
              <v-layout wrap class="py-2">
                <fp-select
                    xs12
                    :items="clientDeals"
                    v-model="selectedDeal"
                    :label="$i18n.useDealPicker.CHOOSE_DEAL"
                    prepend-icon="none"
                    item-name="name_pl"
                    item-key="type"/>
              </v-layout>
            </v-window-item>
            <v-window-item :value="false">
              <deal-badge class="ml-8" :readonly="true" :deal="newDeal"/>
            </v-window-item>
          </v-window>
          <template v-if="!isExistingDeal">
            <deal-from-partner-autocomplete
              v-model="newDeal.partner"
              :readonly-partner="selectedPartner"
              @input="onPartnerChange"/>
            <v-layout v-if="isNestoRo && selectedPartner" wrap class="pt-4">
              <fp-select
                  v-if="sourceListForPartner && sourceListForPartner.length > 0"
                  xs12
                  prepend-icon="mdi-briefcase"
                  v-model="selectedSourceId"
                  :items="sourceListForPartner"
                  item-key="id"
                  item-name="name"
                  :label="sourceLabel"/>
              <fp-select
                  v-if="salesmanListForPartner && salesmanListForPartner.length > 0"
                  xs12
                  prepend-icon="person"
                  v-model="selectedSalesmanId"
                  :items="salesmanListForPartner"
                  item-key="id"
                  item-name="displayName"
                  :label="$i18n.useCreateDeal.SALESMAN"/>
            </v-layout>
          </template>
        </template>
      </v-container>
    </v-card-text>
    <v-divider/>
    <v-card-actions>
      <fp-btn v-if="addOffersToDealMode" primary :disabled="!selectedClient" :click="() => addOffersToDeal()">
        {{ isRaiffeisenOfferSelected && selectedOffersCount > 1 && !hasAccessToRaiffeisen ?
          $i18n.useSimulationResult.ADD_OTHER_OFFERS_TO_DEAL :
          $i18n.useDealPicker.ADD_OFFERS_TO_DEAL}}
      </fp-btn>
      <fp-btn v-else primary :disabled="!selectedClient" :click="() => addSimulationToDeal()">
        {{ $i18n.useDealPicker.LOAD_DEAL }}
      </fp-btn>
    </v-card-actions>
  </v-card>
</template>
<script lang="ts">
import {Component} from 'vue-property-decorator';
import {VueModalComponent} from '@/components/fpComponents/services/ModalService/VueModalComponent';
import Deal from '@/models/Deal';
import {IMultiformDictionary} from '@/modules/multiForm/shared/IMultiformDictionary';
import ClientAutocomplete from '@/components/inputs/ClientAutocomplete.vue';
import DealFromPartnerAutocomplete from '@/components/inputs/DealFromPartnerAutocomplete.vue';
import DealsApi from '@/modules/deals/services/DealsApi';
import Client from '@/models/Client';
import Partner, {FinpackSource, Salesman} from '@/models/Partner';
import PartnerApi from '@/modules/partner/services/PartnerApi';
import {PartnerType} from '@/modules/partner/enums/PartnerType';
import DictionaryStore from '@/commons/services/store/DictionaryStore';
import SimulationApi from '@/modules/calculator-v25/services/shared/SimulationApi';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import { BankSymbol } from '@/commons/dictionaries/BankSymbol';
import { Offer } from "@/models/simulation/subtypes";
import {i18nFormatter} from '@/services/StringFormatter';
const RaiffeisenAccessInfo = () => import(/* webpackChunkName: "RaiffeisenAccessInfo" */ '@/components/calculator/results/RaiffeisenAccessInfo.vue');

@Component({
  components: {DealFromPartnerAutocomplete, ClientAutocomplete, RaiffeisenAccessInfo,},
})
export default class SimulationDealPicker extends VueModalComponent<Deal> {
  isExistingDeal: boolean = false;
  selectedClient: Nullable<Client> = null;
  selectedDeal: Nullable<Deal> = null;
  clientPartner: Nullable<Partner> = null;
  // Component data, not fields on newDeal: Deal's partnerId/partnerSourceId/partnerSalesmanId
  // have no initializers, so with useDefineForClassFields=false they don't exist on the
  // instance when Vue observes it — writes to them would not be reactive.
  selectedPartnerId: Nullable<number> = null;
  selectedSourceId: Nullable<number> = null;
  selectedSalesmanId: Nullable<number> = null;

  newDeal: Deal = new Deal({
    productType: this.modalData.productType,
    simulationId: this.modalData.simulationId,
  });

  addOffersToDealMode: boolean = !!this.modalData.offers;

  clientDeals: IMultiformDictionary<Deal | null> = [];

  readonly dealRadioItems: IMultiformDictionary<boolean> = [
    {type: true, name_pl: I18NGetter().useDealPicker.EXISTING_DEAL,},
    {type: false, name_pl: I18NGetter().useDealPicker.NEW_DEAL,},
  ];

  // Reactive read: getDictionary returns the store array synchronously and
  // triggers a background fetch, so a one-shot copy in mounted() could stay
  // stale (empty) if the modal opens before the dictionary is loaded.
  public get partnersList(): Partner[] {
    return DictionaryStore.getDictionary('partnerDictionary');
  }

  public get isNestoRo(): boolean {
    return this.$env.isAppDomain().NESTO_RO;
  }

  public get selectedPartner(): Nullable<Partner> {
    if (!this.selectedPartnerId) {
      return null;
    }
    return this.partnersList.find(x => x.id === this.selectedPartnerId) ||
      (this.clientPartner?.id === this.selectedPartnerId ? this.clientPartner : null);
  }

  public get sourceListForPartner(): Nullable<Array<FinpackSource>> {
    return this.selectedPartner?.sources || null;
  }

  public get salesmanListForPartner(): Nullable<Array<Salesman>> {
    return this.selectedPartner?.salesman || null;
  }

  public get sourceLabel(): string {
    return this.selectedPartner?.type === PartnerType.INTERNET_WEBSITE
      ? I18NGetter().useCreateDeal.WHAT_WEBSITE : I18NGetter().useCreateDeal.WHAT_INVESTMENT;
  }

  public onPartnerChange(partnerId?: Nullable<number>): void {
    if (!this.isNestoRo) {
      return;
    }
    this.selectedPartnerId = partnerId ?? null;
    this.selectedSourceId = null;
    this.selectedSalesmanId = null;
  }

  private async setPartnerFromClient(client: Client): Promise<void> {
    if (!this.isNestoRo || !client.partnerId) {
      return;
    }
    let partner: Nullable<Partner> = this.partnersList.find(x => x.id === client.partnerId) || null;
    if (!partner) {
      try {
        partner = await PartnerApi.getPartner(client.partnerId);
      } catch (e) {
        console.warn('Could not fetch partner for client', e);
        return;
      }
    }
    this.clientPartner = partner;
    // DealFromPartnerAutocomplete works on ids here (return-object is false)
    this.newDeal.partner = partner.id as unknown as Partner;
    this.selectedPartnerId = partner.id;
    this.selectedSourceId = client.partnerSourceId ?? null;
    this.selectedSalesmanId = null;
  }

  async onClientPicked(client: Nullable<Client>) {
    if (client) {
      if (client?.partnerId) {
        await this.setPartnerFromClient(client);
      }
      const response: Deal[] = await DealsApi.getDealsByClientId(client!.id);
      const activeDeals = response.filter(deal => deal.isPending && deal.productType === this.modalData.productType);
      if (activeDeals.length > 0) {
        this.selectedDeal = activeDeals[0];
        this.isExistingDeal = true;
      } else {
        this.isExistingDeal = false;
      }
      this.clientDeals = activeDeals
        .map(deal => {
          return {
            name_pl: deal.name,
            type: deal,
          };
        });
      this.newDeal.name = i18nFormatter(
        I18NGetter().useDealPicker.NEW_TRANSLATION_NEW_DEAL_NAME, {clientName: client.displayName,}
      );
      this.newDeal.clientIds = [client.id,];
    } else {
      this.selectedDeal = null;
      this.clientDeals = [];
      console.warn('There is no selected client', client);
    }
  }

  async createOrUpdateDeal(): Promise<Deal> {
    if (this.isExistingDeal) {
      return this.selectedDeal?.simulationId
        ? this.selectedDeal!
        : DealsApi.updateDeal({...this.selectedDeal!, simulationId: this.modalData.simulationId,});
    } else {
      if (this.isNestoRo && this.selectedPartnerId) {
        this.newDeal.partnerId = this.selectedPartnerId;
        this.newDeal.partnerSourceId = this.selectedSourceId ?? undefined;
        this.newDeal.partnerSalesmanId = this.selectedSalesmanId ?? undefined;
      }
      return DealsApi.addDeal(this.newDeal);
    }
  }

  async addSimulationToDeal() {
    try {
      const deal = await this.createOrUpdateDeal();
      this.$snackbarService.openSuccessSnackbar(I18NGetter().useDealPicker.DEAL_LOADED);
      this.closeModal(deal);
    } catch (e) {
      this.errorHandler(e, I18NGetter().useDealPicker.LOAD_DEAL_ERROR);
    }
  }

  async addOffersToDeal() {
    const {simulationId, userInput, offers,} = this.modalData;
    const offersToSave: Array<Offer> =
      this.isRaiffeisenOfferSelected && this.selectedOffersCount > 1 && !this.hasAccessToRaiffeisen
        ? offers.filter((offer: Offer) => offer.info.bank !== BankSymbol.RAIFFEISEN) : offers;
    try {
      const {id,}: Deal = await this.createOrUpdateDeal();
      await SimulationApi.addOffersToDeal(id, simulationId, userInput, offersToSave);
      this.$snackbarService.openSuccessSnackbar(I18NGetter().useDealPicker.OFFERS_ADDED);
      if (!this.modalData.additionalInfo) {
        this.$goto.deal(id);
      }

      this.closeModal();
    } catch (e) {
      this.errorHandler(e, I18NGetter().useDealPicker.ADD_OFFERS_TO_DEAL_ERROR);
    }
  }

  get clientHasDeals(): boolean {
    return this.clientDeals.length > 0;
  }

  get hasAccessToRaiffeisen(): boolean {
    return this.$user.privileges.hasAccessToRaiffeisen;
  };

  get isRaiffeisenOfferSelected(): boolean {
    return this.modalData.offers?.some((offer: Offer) => offer.info.bank === BankSymbol.RAIFFEISEN) || false;
  };

  get selectedOffersCount(): number {
    return this.modalData.offers?.length || 0;
  };
}
</script>
